// Lightweight in-memory cache for Dashboard summary data
let cache = {
  data: null,
  timestamp: 0,
  userId: null,
  tenantId: null,
};

let inFlightPromise = null;
const TTL_MS = 60 * 1000; // 60 seconds

const extractIds = (user) => {
  if (!user) return { userId: null, tenantId: null };
  const userId = user._id ? user._id.toString() : user.id ? user.id.toString() : null;
  const tenant = user.tenantId;
  const tenantId = tenant ? (typeof tenant === 'object' && tenant._id ? tenant._id.toString() : tenant.toString()) : null;
  return { userId, tenantId };
};

export const prefetchDashboard = (taskAPI, user) => {
  if (!user || !taskAPI?.dashboard) return null;
  const { userId, tenantId } = extractIds(user);
  if (!userId) return null;

  const now = Date.now();
  // If cache is already valid for this user/tenant within TTL, return resolved promise
  if (
    cache.data &&
    cache.userId === userId &&
    cache.tenantId === tenantId &&
    now - cache.timestamp < TTL_MS
  ) {
    return Promise.resolve(cache.data);
  }

  // If a request is already in-flight, return the shared promise to prevent duplicates
  if (inFlightPromise) {
    return inFlightPromise;
  }

  // Initiate background prefetch
  inFlightPromise = taskAPI.dashboard()
    .then((res) => {
      const summary = res?.data?.data || res?.data || res;
      cache = {
        data: summary,
        timestamp: Date.now(),
        userId,
        tenantId,
      };
      return summary;
    })
    .catch((err) => {
      // Do not cache errors; let subsequent calls handle fallback
      throw err;
    })
    .finally(() => {
      inFlightPromise = null;
    });

  return inFlightPromise;
};

export const getCachedDashboard = (user) => {
  if (!user) return null;
  const { userId, tenantId } = extractIds(user);
  if (!userId) return null;

  const now = Date.now();
  if (
    cache.data &&
    cache.userId === userId &&
    cache.tenantId === tenantId &&
    now - cache.timestamp < TTL_MS
  ) {
    return cache.data;
  }
  return null;
};

export const setCachedDashboard = (user, data) => {
  if (!user || !data) return;
  const { userId, tenantId } = extractIds(user);
  if (!userId) return;

  cache = {
    data,
    timestamp: Date.now(),
    userId,
    tenantId,
  };
};

export const getInFlightDashboardPromise = () => inFlightPromise;

export const invalidateDashboardCache = () => {
  cache = {
    data: null,
    timestamp: 0,
    userId: null,
    tenantId: null,
  };
  inFlightPromise = null;
};
